import React, { useRef, useEffect, useState } from "react";
import { graphql } from "gatsby";
import hljs from "highlight.js";
import "highlight.js/styles/atom-one-dark.css";
import { sampleSize } from "lodash";
import styled from "styled-components";
import * as sanitizeHtml from "sanitize-html";
import { H3Style, SubheadSm } from "storybook-components/Typography";
import { FlexContainer } from "storybook-components/Container";
import { MobileOnlyWrapper } from "storybook-components/Wrapper";
import SEO from "../components/SEO";
import Layout from "../components/Layout";
import ContentContainer from "../components/BlogPost/PostContentContainer";
import IsRichTextValidator from "../components/Helpers/IsRichTextValidator";
import RichTextReadingTime from "../components/Helpers/RichTextReadingTime";
import NotchedImg from "../components/PostPreviewCards/NotchedImg";
import AuthorInfo from "../components/BlogPost/BlogPostAuthorInfo";
import ModeEmbed from "../components/BlogPost/ModeEmbed";
import PublishDate from "../components/BlogPost/BlogPostPublishDate";
import ReadTime from "../components/BlogPost/BlogPostReadTime";
import BlogPostStyleWrapper from "../components/BlogPost/BlogPostStyleWrapper";
import BlogPostRichTextContent from "../components/BlogPost/BlogPostRichTextContent";
import NewsletterCTA from "../components/BlogPost/NewsletterCTA";
import RelatedArticles from "../components/RelatedArticles";
import sanitizeSettings from "../../config/sanitize-html";
import RelatedContentSidebar from "../components/RelatedContentSidebar";

// Styled Components

const PostTitle = styled.h1`
  ${H3Style};
  max-width: 60rem;
  text-align: center;
  margin: 0 0 0.5rem;
  color: ${({ theme }) => theme.colors.green5};
  ${({ theme }) => theme.media.tabletUp`
    font-size: 2.25rem;
  `};
`;

const DateText = styled(SubheadSm)`
  margin: 0 0 4px;
  text-align: center;
  color: ${({ theme }) => theme.colors.gray12};
`;

const BulletSeperator = styled.span`
  display: inline-block;
  margin: 0 5px;
`;

const BorderSeparator = styled.div`
  width: 25rem;
  height: 1px;
  margin: 2rem auto;
  background-color: ${({ theme }) => theme.colors.green5};
`;

// Helper Functions
const checkOgImage = (node, ogImg, heroImg) => {
  if (ogImg) {
    return node.openGraphImage.file.url;
  }
  if (heroImg) {
    return node.heroImage.file.url;
  }
  return null;
};

const checkHeroImage = (node, heroImage) => {
  if (heroImage) {
    return (
      <NotchedImg
        imageData={node.heroImage.gatsbyImageData}
        alt={node.heroImage.title}
        hasBorderRadius
      />
    );
  }
  return null;
};

const checkAuthor = (node, author) => {
  if (author && author.image) {
    return (
      <AuthorInfo
        authorName={node.author.name}
        authorTitle={node.author.title}
        authorImg={node.author.image.file.url}
      />
    );
  }
  if (author) {
    return (
      <AuthorInfo
        authorName={node.author.name}
        authorTitle={node.author.title}
      />
    );
  }
  return null;
};

const relatedArticlesSection = relatedArticles => {
  if (relatedArticles.length > 0) {
    return <RelatedArticles relatedArticles={relatedArticles} />;
  }
  return null;
};

// BlogPostTemplate Component
const BlogPostTemplate = ({ data }) => {
  const blogPost = data.mainPost;
  const description = blogPost.description
    ? blogPost.description.description
    : "Mode blog";
  const legacyBodyReadTime =
    blogPost.body &&
    blogPost.body.childMarkdownRemark &&
    blogPost.body.childMarkdownRemark.timeToRead;
  const legacyBodyHtml =
    blogPost.body &&
    blogPost.body.childMarkdownRemark &&
    blogPost.body.childMarkdownRemark.html;
  // Post content could either be legacy markdown, or new contentful rich text field
  const isRichTextPost = IsRichTextValidator(blogPost.richTextBody);
  const [richTextReadingTime, setRichTextReadingTime] = useState({});
  const randomizedRelatedPosts = data.relatedArticles
    ? sampleSize(data.relatedArticles.edges, 2)
    : [];
  const blogArticleContent = useRef();

  // Code block highlighting for legacy markdown posts
  useEffect(() => {
    if (blogArticleContent && blogArticleContent.current) {
      hljs.highlightAll();
    }
  }, [blogArticleContent, hljs]);

  return (
    <Layout
      blogPostData={blogPost}
      centerContent
      hideFinalCta
      showRelatedContent
    >
      {isRichTextPost && (
        <RichTextReadingTime
          richTextRaw={blogPost.richTextBody.raw}
          callback={setRichTextReadingTime}
        />
      )}
      <SEO
        title={blogPost.seoTitle || blogPost.title}
        description={description}
        image={checkOgImage(
          blogPost,
          blogPost.openGraphImage,
          blogPost.heroImage
        )}
        pathname={`/${blogPost.slug}`}
        objectType="article"
      />
      <FlexContainer>
        <ContentContainer>
          <PostTitle>{blogPost.title}</PostTitle>
          {checkAuthor(blogPost, blogPost.author)}
          <DateText>
            <PublishDate publishDate={blogPost.publishDate} />
            {blogPost.updatedOnDate && (
              <>
                <BulletSeperator>&bull;</BulletSeperator>
                <span>Updated on </span>
                <PublishDate publishDate={blogPost.updatedOnDate} />
              </>
            )}
          </DateText>
          <ReadTime
            readTime={
              isRichTextPost
                ? Math.ceil(richTextReadingTime.minutes)
                : legacyBodyReadTime
            }
          />
          {checkHeroImage(blogPost, blogPost.heroImage)}
          <BorderSeparator />
          <ModeEmbed>
            {isRichTextPost ? (
              <BlogPostRichTextContent
                richTextContent={blogPost.richTextBody}
              />
            ) : (
              <BlogPostStyleWrapper
                className="post-body"
                ref={blogArticleContent}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(legacyBodyHtml, sanitizeSettings)
                }}
              />
            )}
          </ModeEmbed>
          <NewsletterCTA />
          <MobileOnlyWrapper>
            <RelatedContentSidebar />
          </MobileOnlyWrapper>
        </ContentContainer>
      </FlexContainer>

      {relatedArticlesSection(randomizedRelatedPosts)}
    </Layout>
  );
};

export default BlogPostTemplate;

export const query = graphql`
  query blogPostQuery($slug: String!, $category: String!) {
    mainPost: contentfulBlogPost(slug: { eq: $slug }) {
      title
      seoTitle
      slug
      publishDate
      updatedOnDate
      categories
      heroImage {
        title
        gatsbyImageData(layout: FULL_WIDTH)
        file {
          url
        }
      }
      author {
        name
        title
        twitter
        image {
          file {
            url
          }
        }
      }
      openGraphImage {
        file {
          url
        }
      }
      description {
        description
      }
      body {
        childMarkdownRemark {
          html
          timeToRead
        }
      }
      richTextBody {
        raw
        references {
          ... on Node {
            ... on ContentfulAsset {
              contentful_id
              title
              __typename
              gatsbyImageData(width: 1600)
              file {
                url
                contentType
              }
            }
            ... on ContentfulCodeBlock {
              contentful_id
              __typename
              name
              code {
                code
              }
            }
            ... on ContentfulYoutubeVideo {
              contentful_id
              __typename
              name
              embedUrl
            }
            ... on ContentfulBlockQuote {
              contentful_id
              __typename
              quote {
                quote
              }
              byline
            }
            ... on ContentfulCtaButton {
              contentful_id
              __typename
              buttonText
              linkUrl
            }
            ... on ContentfulCtaLink {
              contentful_id
              __typename
              linkText
              linkUrl
            }
            ... on ContentfulContentLinkCard {
              contentful_id
              __typename
              name
              cta
              title
              url
              description
              buttonText
              image {
                gatsbyImageData(width: 260)
              }
            }
          }
        }
      }
    }
    popularPosts: allContentfulBlogPost(
      limit: 5
      filter: { isPopular: { eq: true } }
    ) {
      edges {
        node {
          title
          slug
          categories
        }
      }
    }
    relatedArticles: allContentfulBlogPost(
      filter: {
        categories: { eq: $category }
        slug: { ne: $slug }
        publishDate: { gt: "2019-01-01T00:00-08:00" }
      }
    ) {
      edges {
        node {
          publishDate
          title
          slug
          heroImage {
            title
            gatsbyImageData(layout: FULL_WIDTH)
            file {
              url
            }
          }
          body {
            childMarkdownRemark {
              timeToRead
            }
          }
        }
      }
    }
  }
`;
